import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { TextInputField, FormSubmitButton, KITCHEN_FLOW_ONLY_ROLES, RIDER_APP_ONLY_ROLES } from '@/components/forms/FormComponents'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { useRiderAppEnabled } from '@/hooks/useRiderAppEnabled'
import { createUserSchema, updateUserSchema, type CreateUserData, type UpdateUserData } from '@/lib/form-schemas'
import { toastHelpers } from '@/lib/toast-helpers'
import { cn } from '@/lib/utils'
import apiClient from '@/api/client'
import type { User } from '@/types'
import { Camera, X } from 'lucide-react'

interface UserFormProps {
  user?: User // If provided, we're editing; otherwise creating
  onSuccess?: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
}

/**
 * Role tiles. One plain line per role on what that person can open — this is
 * the most consequential choice on the form, so all seven are visible at once
 * instead of hidden behind a dropdown. Keep the value set in step with
 * `lib/staff-roles.ts` (the canonical role list).
 */
const ROLE_TILES: ReadonlyArray<{ value: CreateUserData['role']; name: string; blurb: string }> = [
  { value: 'admin', name: 'Administrator', blurb: 'Everything, including settings and staff' },
  { value: 'manager', name: 'Manager', blurb: 'Counter, menu, tables, staff, day close, reports' },
  { value: 'counter', name: 'Counter', blurb: 'Take orders and payments at the till' },
  { value: 'server', name: 'Server', blurb: 'Table orders and Fire KOT. No payments' },
  { value: 'kitchen', name: 'Kitchen', blurb: 'Kitchen display and stations only' },
  { value: 'inventory_manager', name: 'Inventory manager', blurb: 'Stock and inventory only' },
  { value: 'accountant', name: 'Accountant', blurb: 'Reports and expenses, read only' },
  // Kitchen flow (lane C): only offered while kitchen_flow is on — see visibleRoleTiles.
  { value: 'kitchen_manager', name: 'Kitchen manager', blurb: 'Asks the store for stock, receives it, runs the kitchen screen' },
  // Delivery riders: only offered while feature_rider_app is on.
  { value: 'rider', name: 'Rider', blurb: 'Their own delivery queue on their phone. Nothing else' },
]

function roleName(value: string | undefined): string {
  return ROLE_TILES.find((r) => r.value === value)?.name ?? ''
}

function initials(first?: string, last?: string): string {
  const a = (first ?? '').trim().charAt(0)
  const b = (last ?? '').trim().charAt(0)
  return (a + b).toUpperCase() || '?'
}

export function UserForm({ user, onSuccess, onCancel, mode = 'create' }: UserFormProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const kitchenFlow = useKitchenFlowEnabled()
  const riderApp = useRiderAppEnabled()
  // A flag-only role is hidden unless the flag is on — except on a user who
  // already holds it, so editing them never blanks their role.
  const visibleRoleTiles = ROLE_TILES.filter(
    (t) =>
      user?.role === t.value ||
      ((kitchenFlow || !KITCHEN_FLOW_ONLY_ROLES.has(t.value)) && (riderApp || !RIDER_APP_ONLY_ROLES.has(t.value))),
  )
  const isEditing = mode === 'edit' && user

  // Password is an action when editing: the field only appears once someone
  // asks to reset it, so nobody types into an always-empty box by mistake.
  const [resettingPassword, setResettingPassword] = useState(false)
  // "Paste an image link" reveals a small URL input under the header.
  const [linkOpen, setLinkOpen] = useState(false)

  // Choose the appropriate schema and default values
  const schema = isEditing ? updateUserSchema : createUserSchema
  const defaultValues = isEditing
    ? {
        id: user.id,
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role as any,
        password: '', // Don't pre-fill password for editing
        profile_image_url: user.profile_image_url ?? '',
        email_reports_enabled: user.email_reports_enabled ?? false,
        // "" (not null) is what the API sends back for "no number" — see
        // types/index.ts. Passing it straight through means an untouched
        // form resubmits the same "no number" state rather than clearing one.
        whatsapp_phone: user.whatsapp_phone ?? '',
        whatsapp_reports_enabled: user.whatsapp_reports_enabled ?? false,
      }
    : {
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'counter' as const,
        profile_image_url: '',
        // Off by default: a new account should never start out receiving the
        // restaurant's sales figures without someone deciding it should.
        email_reports_enabled: false,
        whatsapp_phone: '',
        whatsapp_reports_enabled: false,
      }

  const form = useForm<CreateUserData | UpdateUserData>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateUserData) => {
      const payload = { ...data } as Record<string, unknown>
      if (!String(payload.profile_image_url ?? '').trim()) delete payload.profile_image_url
      return apiClient.createUser(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toastHelpers.userCreated(`${form.getValues('first_name')} ${form.getValues('last_name')}`)
      form.reset()
      onSuccess?.()
    },
    onError: (error) => {
      toastHelpers.apiError('Create user', error)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateUserData) => {
      const payload = { ...data } as Record<string, unknown>
      if (payload.profile_image_url !== undefined && !String(payload.profile_image_url ?? '').trim()) {
        payload.profile_image_url = ''
      }
      return apiClient.updateUser(String(data.id), payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toastHelpers.apiSuccess('Save', `User ${form.getValues('first_name')} ${form.getValues('last_name')}`)
      onSuccess?.()
    },
    onError: (error) => {
      toastHelpers.apiError('Save user', error)
    },
  })

  const onSubmit = (data: CreateUserData | UpdateUserData) => {
    if (isEditing) {
      // Filter out empty password for updates
      const updateData = { ...data } as UpdateUserData
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password
      }
      updateMutation.mutate(updateData)
    } else {
      createMutation.mutate(data as CreateUserData)
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  // Header preview follows what is typed, so the avatar and subtitle always
  // describe the record as it will be saved.
  const watchedFirst = form.watch('first_name')
  const watchedLast = form.watch('last_name')
  const watchedUsername = form.watch('username')
  const watchedRole = form.watch('role')
  const watchedPhoto = form.watch('profile_image_url')
  const hasPhoto = Boolean(String(watchedPhoto ?? '').trim())

  const setPhoto = (value: string) => {
    form.setValue('profile_image_url', value, { shouldValidate: true, shouldDirty: true })
  }

  const clearPhoto = () => {
    setPhoto('')
    setLinkOpen(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const subtitle = [
    `${watchedFirst ?? ''} ${watchedLast ?? ''}`.trim(),
    roleName(watchedRole),
    watchedUsername ? `@${watchedUsername}` : '',
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Card className="w-full max-w-2xl mx-auto overflow-hidden">
      {/* Header: avatar (photo lives here), title, close */}
      <div className="flex items-center gap-4 border-b px-6 py-5">
        <div className="relative flex-none">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Change photo"
                className="group relative h-14 w-14 rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {hasPhoto ? (
                  <img
                    src={String(watchedPhoto)}
                    alt=""
                    className="h-14 w-14 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
                    {initials(watchedFirst, watchedLast)}
                  </span>
                )}
                <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm group-hover:text-foreground">
                  <Camera className="h-3 w-3" />
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onSelect={() => fileInputRef.current?.click()}>
                Upload a photo
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setLinkOpen(true)}>
                Paste an image link
              </DropdownMenuItem>
              {hasPhoto && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={clearPhoto}>
                    Remove photo
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            id="staff-profile-file"
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            className="sr-only"
            tabIndex={-1}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (!f) return
              if (f.size > 350_000) {
                toastHelpers.error('Image too large', 'Use a file under 350KB, or paste an image link instead.')
                e.target.value = ''
                return
              }
              const reader = new FileReader()
              reader.onload = () => {
                setPhoto(String(reader.result || ''))
                setLinkOpen(false)
              }
              reader.readAsDataURL(f)
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold leading-tight tracking-tight">
            {isEditing ? 'Edit user' : 'New user'}
          </h2>
          <p className="mt-0.5 truncate text-sm text-muted-foreground">
            {subtitle || 'Fill in the details below'}
          </p>
        </div>
        {onCancel && (
          <Button variant="ghost" size="icon" onClick={onCancel} disabled={isLoading} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <CardContent className="p-0">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="divide-y px-6">
              {linkOpen && (
                <section className="py-4">
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <TextInputField
                        control={form.control}
                        name="profile_image_url"
                        label="Image link"
                        placeholder="https://example.com/photo.jpg"
                      />
                    </div>
                    <Button type="button" variant="ghost" size="sm" className="mb-0.5" onClick={() => setLinkOpen(false)}>
                      Done
                    </Button>
                  </div>
                </section>
              )}

              {/* Details */}
              <section className="py-5">
                <p className="mb-3 text-xs font-semibold text-muted-foreground">Details</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <TextInputField
                    control={form.control}
                    name="first_name"
                    label="First name"
                    placeholder="First name"
                    autoComplete="given-name"
                  />
                  <TextInputField
                    control={form.control}
                    name="last_name"
                    label="Last name"
                    placeholder="Last name"
                    autoComplete="family-name"
                  />
                </div>
                <div className="mt-3">
                  <TextInputField
                    control={form.control}
                    name="email"
                    label="Email"
                    type="email"
                    placeholder="name@example.com"
                    autoComplete="email"
                  />
                </div>
              </section>

              {/* Sign in */}
              <section className="py-5">
                <p className="mb-3 text-xs font-semibold text-muted-foreground">Sign in</p>
                <TextInputField
                  control={form.control}
                  name="username"
                  label="Username"
                  placeholder="Username"
                  autoComplete="username"
                />
                <div className="mt-3">
                  {isEditing && !resettingPassword ? (
                    <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/40 px-3 py-2.5">
                      <div>
                        <Label className="text-sm font-medium">Password</Label>
                        <div className="text-sm tracking-[0.2em] text-muted-foreground">••••••••</div>
                      </div>
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-sm"
                        onClick={() => setResettingPassword(true)}
                      >
                        Reset password
                      </Button>
                    </div>
                  ) : (
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel>{isEditing ? 'New password' : 'Password'}</FormLabel>
                            {isEditing && (
                              <Button
                                type="button"
                                variant="link"
                                className="h-auto p-0 text-sm"
                                onClick={() => {
                                  form.setValue('password', '', { shouldValidate: true })
                                  form.clearErrors('password')
                                  setResettingPassword(false)
                                }}
                              >
                                Keep current password
                              </Button>
                            )}
                          </div>
                          <FormControl>
                            <Input
                              {...field}
                              type="password"
                              placeholder="At least 6 characters"
                              autoComplete="new-password"
                              autoFocus={!!isEditing}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </section>

              {/* Access */}
              <section className="py-5">
                <p className="mb-3 text-xs font-semibold text-muted-foreground">Access</p>
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div role="radiogroup" aria-label="Role" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {visibleRoleTiles.map((tile) => {
                            const on = field.value === tile.value
                            return (
                              <button
                                key={tile.value}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                onClick={() => field.onChange(tile.value)}
                                className={cn(
                                  'relative rounded-lg border px-3 py-2.5 pl-9 text-left transition-colors',
                                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background',
                                  on
                                    ? 'border-primary bg-primary/5'
                                    : 'border-border bg-card hover:border-muted-foreground/40'
                                )}
                              >
                                <span
                                  aria-hidden
                                  className={cn(
                                    'absolute left-3 top-3 h-3.5 w-3.5 rounded-full border',
                                    on
                                      ? 'border-primary bg-primary shadow-[inset_0_0_0_3px_hsl(var(--card))]'
                                      : 'border-muted-foreground/50 bg-background'
                                  )}
                                />
                                <span className="block text-sm font-semibold leading-tight">{tile.name}</span>
                                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{tile.blurb}</span>
                              </button>
                            )
                          })}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/*
                  F-RPT-N16 — day-close report email. Sits under Access because
                  it is the same kind of decision: what this person gets to see.
                  The report carries full sales, tax and variance figures, so it
                  is worth a thought before switching on for a floor account.
                */}
                <FormField
                  control={form.control}
                  name="email_reports_enabled"
                  render={({ field }) => (
                    <FormItem className="mt-4 flex flex-row items-center justify-between gap-4 border-t border-dashed pt-4">
                      <div className="space-y-0.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <FormLabel className="cursor-help text-sm font-medium underline decoration-dotted underline-offset-4">
                              Email the daily Z report
                            </FormLabel>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            Sent about 30 minutes after Day Close so late corrections are included.
                            Contains full sales, tax and till-variance figures.
                          </TooltipContent>
                        </Tooltip>
                        <p className="text-xs text-muted-foreground">
                          Sales, tenders and till variance after every Day Close
                        </p>
                      </div>
                      <FormControl>
                        <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/*
                  F-RPT-N40 — the same Z report over WhatsApp. Number and opt-in
                  are separate so a tenant can pause an owner's reports without
                  discarding their number, then switch it back on in one tap.
                */}
                <FormField
                  control={form.control}
                  name="whatsapp_phone"
                  render={({ field }) => (
                    <FormItem className="mt-4 border-t border-dashed pt-4">
                      <FormLabel className="text-sm font-medium">WhatsApp number</FormLabel>
                      <FormControl>
                        <Input placeholder="0300 1234567" {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Pakistani number. Saved as +92…
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="whatsapp_reports_enabled"
                  render={({ field }) => (
                    <FormItem className="mt-4 flex flex-row items-center justify-between gap-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-medium">WhatsApp the daily Z report</FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Same figures as the email, without the PDF
                        </p>
                      </div>
                      <FormControl>
                        <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </section>
            </div>

            {/* Footer: one clear save */}
            <div className="flex items-center justify-end gap-2 border-t bg-card px-6 py-4">
              {onCancel && (
                <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
                  Cancel
                </Button>
              )}
              <FormSubmitButton
                isLoading={isLoading}
                loadingText={isEditing ? 'Saving…' : 'Creating…'}
              >
                {isEditing ? 'Save changes' : 'Create user'}
              </FormSubmitButton>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
